/**
 * AgentDebugPanel — shows real-time tool calls and the agent's skill toolbelt.
 * Three tabs: Tools (call/result timeline), Skills (toolbelt + runtime
 * activations), and Tokens (live token + cost tracking for this conversation).
 */

import { useState, useEffect, useRef, type MouseEvent, type ReactNode } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import './AgentDebugPanel.css';
import { AgentDebugTokensTab } from './AgentDebugTokensTab';
import { useFeature } from '@/hooks/useFeature';

export interface ToolDebugEvent {
  type: 'tool_debug';
  action: 'call' | 'result';
  tool: string;
  args?: Record<string, unknown>;
  preview?: string;
  author?: string;
  timestamp?: number;
  // When observability.tool_invocations is on, the backend stamps every
  // event with the persisted row id so a refresh can rehydrate from
  // /conversations/<id>/tool-invocations.
  invocation_id?: string | null;
}

export interface ActiveSkillEntry {
  name: string;
  activatedAt: number;
  messageIndex: number;  // which message triggered it
}

export interface BotSkill {
  skill_id: string;
  skill_name: string;
}

interface AgentDebugPanelProps {
  events: ToolDebugEvent[];
  // The bot's static toolbelt — every skill attached to the chatbot,
  // regardless of whether it has activated in this conversation.
  botSkills: BotSkill[];
  // Runtime activations during this conversation — used to badge the
  // skills in the toolbelt that have actually fired so far.
  activeSkillEntries: ActiveSkillEntry[];
  open: boolean;
  onClose: () => void;
  // Conversation id the panel is scoped to. When set, the Tokens tab
  // queries /insights/conversations/<id>/summary + /timeline for live
  // token usage. NULL = tab still renders but with a "no data" hint.
  conversationId?: string | null;
  // Tenant id forwarded to the cost endpoints' tenant_id query param.
  tenantId?: string;
}

type TabKey = 'tools' | 'skills' | 'tokens';

export function AgentDebugPanel({ events, botSkills, activeSkillEntries, open, onClose, conversationId, tenantId }: AgentDebugPanelProps) {
  const { t } = useLanguage();
  const bottomRef = useRef<HTMLDivElement>(null);
  const [expandedSet, setExpandedSet] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState<TabKey>('tools');
  // Per-tenant flag — when OFF, the Tokens tab is hidden (the conversation
  // still emits cost events to the ledger; this just controls whether the
  // user can see them inside the per-conversation Dev Tools panel).
  const canSeeTokensTab = useFeature('observability.dev_tools_tokens');
  // If the flag flipped off while the user was on the Tokens tab, bounce.
  useEffect(() => {
    if (!canSeeTokensTab && activeTab === 'tokens') setActiveTab('tools');
  }, [canSeeTokensTab, activeTab]);

  useEffect(() => {
    if (open && bottomRef.current && activeTab === 'tools') {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [events.length, open, activeTab]);

  useEffect(() => {
    if (events.length > 0) {
      setExpandedSet(prev => {
        const next = new Set(prev);
        next.add(events.length - 1);
        return next;
      });
    }
  }, [events.length]);

  const toggleExpand = (idx: number) => {
    setExpandedSet(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const expandAll = () => setExpandedSet(new Set(events.map((_, i) => i)));
  const collapseAll = () => setExpandedSet(new Set());

  if (!open) return null;

  return (
    <div className="agent-debug-overlay" onClick={onClose}>
      <div className="agent-debug-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header — collapse chevron on the inline-start (points toward the
            panel's docked edge, so it reads "slide the panel away"; the X in
            the top corner collided with the floating support (i) button),
            title centered, bulk actions on the inline-end. */}
        <div className="agent-debug-header">
          <button className="agent-debug-close" onClick={onClose} title={t('common.close')} aria-label={t('common.close')}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>
          <div className="agent-debug-title">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="16 18 22 12 16 6"/>
              <polyline points="8 6 2 12 8 18"/>
            </svg>
            <span>{t('agentDebug.devTools')}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="agent-debug-tabs">
          <button
            className={`agent-debug-tab ${activeTab === 'tools' ? 'active' : ''}`}
            onClick={() => setActiveTab('tools')}
          >
            Tools
            {/* Count excludes activate_skill events — those belong on the
                Skills tab and shouldn't bloat the Tools counter. */}
            {(() => {
              const n = events.filter(e => e.tool !== 'activate_skill').length;
              return n > 0 ? <span className="agent-debug-tab-count">{n}</span> : null;
            })()}
          </button>
          <button
            className={`agent-debug-tab ${activeTab === 'skills' ? 'active' : ''}`}
            onClick={() => setActiveTab('skills')}
          >
            Skills
            {/* Count = distinct skills activated via activate_skill
                calls or skill_activation SSE events. Built-in tools
                like knowledge_search are NOT counted here — they
                live on the Tools tab. */}
            {(() => {
              const keys = new Set<string>();
              for (const ev of events) {
                if (ev.action === 'call' && ev.tool === 'activate_skill') {
                  const name = ev.args?.skill_name;
                  if (typeof name === 'string' && name.trim()) {
                    keys.add(normalizeSkillKey(name));
                  }
                }
              }
              for (const e of activeSkillEntries) {
                if (e.name && e.name.trim()) keys.add(normalizeSkillKey(e.name));
              }
              return keys.size > 0
                ? <span className="agent-debug-tab-count">{keys.size}</span>
                : null;
            })()}
          </button>
          {canSeeTokensTab && (
            <button
              className={`agent-debug-tab ${activeTab === 'tokens' ? 'active' : ''}`}
              onClick={() => setActiveTab('tokens')}
            >
              {t('insights.pulse.tokens')}
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div className="agent-debug-body">
          {activeTab === 'tokens' && canSeeTokensTab ? (
            <AgentDebugTokensTab
              conversationId={conversationId ?? null}
              refetchSignal={events.length}
              tenantId={tenantId}
            />
          ) : activeTab === 'skills' ? (
            <SkillsTab botSkills={botSkills} activeSkillEntries={activeSkillEntries} events={events} />
          ) : (
            <>
              {(() => {
                // Drop activate_skill events — those are skill activations,
                // not "real" tool calls. They surface in the Skills tab via
                // their skill_name arg. Hiding them here keeps the Tools
                // timeline focused on function-tool calls (knowledge_search,
                // web_search, custom tools, etc.).
                const toolEvents = events.filter(e => e.tool !== 'activate_skill');
                if (toolEvents.length === 0) {
                  return (
                    <div className="agent-debug-empty">
                      {t('chat.noToolCallsYetSend')}
                    </div>
                  );
                }
                return (
                  <>
                    {/* Tools-tab toolbar. Lives INSIDE the tab body (not the
                        panel header) so it can never collide with the floating
                        support (i) button pinned to the window corner. */}
                    <div className="agent-debug-toolbar">
                      <button className="agent-debug-action-btn" onClick={expandAll} title={t('agentDebug.expandAll')}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="7 13 12 18 17 13"/>
                          <polyline points="7 6 12 11 17 6"/>
                        </svg>
                      </button>
                      <button className="agent-debug-action-btn" onClick={collapseAll} title={t('agentDebug.collapseAll')}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="17 11 12 6 7 11"/>
                          <polyline points="17 18 12 13 7 18"/>
                        </svg>
                      </button>
                    </div>
                    {toolEvents.map((ev, i) => {
                  const body = renderEventBody(ev);
                  const isExpanded = expandedSet.has(i);
                  const hasContent = body !== null;

                  return (
                    <div key={i} className={`agent-debug-event agent-debug-event--${ev.action}`}>
                      <div
                        className={`agent-debug-event-header ${hasContent ? 'clickable' : ''} ${isExpanded ? 'is-expanded' : 'is-collapsed'}`}
                        onClick={() => hasContent && toggleExpand(i)}
                      >
                        <span className={`agent-debug-badge agent-debug-badge--${ev.action}`} dir="auto">
                          {ev.action === 'call' ? t('agentDebug.input') : t('agentDebug.output')}
                        </span>
                        <span className="agent-debug-tool-name">{ev.tool}</span>
                        {ev.author && ev.author !== 'Assistant' && (
                          <span className="agent-debug-author">{ev.author}</span>
                        )}
                        <span className="agent-debug-header-spacer" />
                        {hasContent && isExpanded && body && (
                          <CopyButton text={body.copyText} compact />
                        )}
                        {hasContent && (
                          <span className={`agent-debug-chevron ${isExpanded ? 'expanded' : ''}`}>
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 12 15 18 9"/>
                            </svg>
                          </span>
                        )}
                      </div>
                      {isExpanded && body && (
                        <div className={`agent-debug-content agent-debug-content--${ev.action}`} dir="auto">
                          {body.node}
                        </div>
                      )}
                    </div>
                  );
                    })}
                  </>
                );
              })()}
              <div ref={bottomRef} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Skills Tab ────────────────────────────────────── */
//
// Lists ONLY skills that the agent activated this conversation —
// never built-in tool calls. A skill counts as activated if either:
//
//   - The model called `activate_skill(skill_name="X")` — that's the
//     sub-agent / persona activation path. We pull skill_name from
//     the call's args so the same skill stacks across multiple
//     activations into one row with a count.
//
//   - A runtime `skill_activation` SSE event fired during the live
//     stream (live-only — not persisted in tool_invocations).
//
// Display name prefers the human-readable bot skill_name when we
// can match it; otherwise falls back to the raw skill_name string
// the model passed.

function SkillsTab({
  botSkills,
  activeSkillEntries,
  events,
}: {
  botSkills: BotSkill[];
  activeSkillEntries: ActiveSkillEntry[];
  events: ToolDebugEvent[];
}) {
  const { t } = useLanguage();
  // Index the bot's user-added skills so we can resolve display names
  // from the normalized key the model passed.
  const skillByKey = new Map<string, BotSkill>();
  for (const s of botSkills) {
    skillByKey.set(normalizeSkillKey(s.skill_name), s);
  }

  // Map normalized name → display data.
  const activated = new Map<string, { display: string; count: number }>();

  const bump = (rawName: string) => {
    const trimmed = rawName.trim();
    if (!trimmed) return;
    const key = normalizeSkillKey(trimmed);
    const skill = skillByKey.get(key);
    const display = skill?.skill_name ?? trimmed;
    const entry = activated.get(key);
    if (entry) {
      entry.count += 1;
    } else {
      activated.set(key, { display, count: 1 });
    }
  };

  // Persisted / live activate_skill calls — extract the target skill
  // from args.skill_name. Multiple calls to the same skill stack.
  for (const ev of events) {
    if (ev.action !== 'call') continue;
    if (ev.tool !== 'activate_skill') continue;
    const skillName = ev.args?.skill_name;
    if (typeof skillName === 'string') bump(skillName);
  }

  // Live SSE skill_activation events. Don't double-count when an
  // activate_skill tool call already registered the same name —
  // bump() dedupes by normalized key.
  for (const e of activeSkillEntries) {
    bump(e.name);
  }

  const rows = Array.from(activated.values());

  if (rows.length === 0) {
    return (
      <div className="agent-debug-empty">{t('agentDebug.noSkillsActivated')}</div>
    );
  }

  return (
    <div className="skills-tab">
      <div className="tokens-section-title">
        {t('agentDebug.activatedInThisConversation')}
        <span className="tokens-section-count">{rows.length}</span>
      </div>
      <div className="skills-list">
        {rows.map((entry) => (
          <div key={entry.display} className="skills-entry">
            <div className="skills-entry-icon">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
            </div>
            <div className="skills-entry-name">{entry.display}</div>
            <div className="skills-entry-status">
              {t('agentDebug.skillCount', { count: entry.count })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Bot skill_name is human-readable ("Knowledge Search") but the persisted
// / live tool name is the ADK function key ("knowledge_search"). Backend
// normalizes via `name.lower().strip().replace(' ', '_')` in
// resolve_tool_name (registry.py). Mirror it here so comparisons match
// across the two surfaces instead of silently failing.
function normalizeSkillKey(name: string): string {
  return name.toLowerCase().trim().replace(/\s+/g, '_').replace(/-/g, '_');
}

/* ── Copy Button ───────────────────────────────────── */

function CopyButton({
  text,
  compact = false,
  inline = false,
  title,
}: {
  text: string;
  compact?: boolean;
  inline?: boolean;
  title?: string;
}) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail under non-HTTPS / non-focus conditions —
      // fall back to the legacy execCommand path.
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {
        /* dev-tooling — silent failure is fine */
      }
    }
  };

  const cls = [
    'agent-debug-copy-btn',
    compact ? 'agent-debug-copy-btn--compact' : '',
    inline ? 'agent-debug-copy-btn--inline' : '',
    copied ? 'agent-debug-copy-btn--copied' : '',
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={cls}
      onClick={handleCopy}
      title={title ?? (copied ? t('common.copiedExclaim') : t('common.copyToClipboard'))}
      aria-label={copied ? t('common.copied') : t('common.copy')}
    >
      {copied ? (
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
        </svg>
      )}
      {!inline && <span>{copied ? t('common.copied') : t('common.copy')}</span>}
    </button>
  );
}

/* ── Body rendering ───────────────────────────────── */
//
// Each event renders ONE body. The body shape depends on what the
// model actually did:
//
//   - CALL events  → simple `key: value` list of the args the model
//     passed. Empty / blank values are skipped to keep the surface
//     minimal.
//
//   - RESULT events of `knowledge_search` → a domain-specific view
//     showing the summary line + a "Semantic results" section + a
//     "Lexical results" section. The full retrieved context (the 19K
//     character `context` field the LLM reads) is hidden — the dev
//     panel only needs to know WHICH chunks matched and via which
//     retrieval layer, not their full text.
//
//   - RESULT of any other tool → plain text (truncated at the bottom
//     if it's gigantic, so the body never grows unbounded).
//
// Each renderer returns { node, copyText }. `copyText` is what the
// Copy button in the event header writes to the clipboard — the raw
// payload, so devs can grab the full data even when the visible body
// is summarised.

interface EventBody {
  node: ReactNode;
  copyText: string;
}

function renderEventBody(ev: ToolDebugEvent): EventBody | null {
  if (ev.action === 'call') {
    const args = ev.args;
    if (!args || Object.keys(args).length === 0) return null;
    return {
      node: <ArgsList args={args} />,
      copyText: safeStringify(args),
    };
  }

  // result
  const preview = (ev.preview ?? '').trim();
  if (!preview) return null;

  if (ev.tool === 'knowledge_search') {
    const parsed = tryParseKnowledgeSearchOutput(preview);
    if (parsed) {
      return {
        node: <KnowledgeSearchResultView data={parsed} />,
        copyText: safeStringify(parsed),
      };
    }
    // Fall through to generic text view if the payload didn't parse
    // (e.g. an error string from the tool).
  }

  return {
    node: <GenericResultView text={preview} />,
    copyText: preview,
  };
}

function ArgsList({ args }: { args: Record<string, unknown> }) {
  // Show EVERY arg the model passed, including empty strings and
  // explicit nulls. An unset `file_name` is itself a signal — the
  // model chose to search across all files, not just one — so the
  // panel surfaces it as "(empty)" rather than hiding the row.
  // We only drop `undefined` keys (those mean "not present at all").
  const rows = Object.entries(args).filter(([, v]) => v !== undefined);

  if (rows.length === 0) {
    return <div className="agent-debug-empty-inline">(no arguments)</div>;
  }

  return (
    <div className="agent-debug-fields">
      {rows.map(([k, v]) => (
        <ArgField key={k} name={k} value={v} />
      ))}
    </div>
  );
}

// Each arg renders as a labeled "field" — name on top, value boxed
// below in a monospace block. Long string values get their own
// max-height + scroll inside the box so a 2KB query doesn't blow up
// the whole INPUT block. Empty / null values render a muted
// placeholder so the field is always visible.
function ArgField({ name, value }: { name: string; value: unknown }) {
  const isEmptyString = typeof value === 'string' && value.length === 0;
  const isNullish = value === null;
  const text = formatScalar(value);
  const isLong = text.length > 120 || text.includes('\n');

  return (
    <div className="agent-debug-field">
      <div className="agent-debug-field-label">
        <span className="agent-debug-field-name">{name}</span>
        <span className="agent-debug-field-type">{describeType(value)}</span>
        <CopyButton text={text} compact title={`Copy ${name}`} />
      </div>
      <div
        className={`agent-debug-field-value ${isLong ? 'is-long' : ''} ${isEmptyString || isNullish ? 'is-blank' : ''}`}
      >
        {isEmptyString ? '(empty string)' : isNullish ? '(null)' : text}
      </div>
    </div>
  );
}

function formatScalar(v: unknown): string {
  if (v === null) return 'null';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

function describeType(v: unknown): string {
  if (v === null) return 'null';
  if (Array.isArray(v)) return `array · ${v.length}`;
  if (typeof v === 'string') return v.length === 0 ? 'string · empty' : `string · ${v.length} chars`;
  return typeof v;
}

/* ── knowledge_search OUTPUT ────────────────────────── */

interface KnowledgeSearchOutput {
  summary?: string;
  semantic_hits?: KnowledgeSearchHit[];
  lexical_hits?: KnowledgeSearchHit[];
  breakdown?: KnowledgeSearchBreakdown;
}

interface KnowledgeSearchHit {
  filename?: string;
  semantic_rank?: number | null;
  semantic_score?: number | null;
  fts_rank?: number | null;
  fts_score?: number | null;
  snippet?: string;
}

interface KnowledgeSearchBreakdown {
  total_chunks?: number;
  semantic_only?: number;
  lexical_only?: number;
  both_methods?: number;
}

// The ADK function_response payload arrives as a JSON object that
// wraps the actual tool return — sometimes shaped as `{"result": "<json
// string>"}`, sometimes as `{"result": {...}}`, occasionally raw. Try
// the variations, recursively unfold nested JSON strings, and pull out
// the knowledge_search-shaped keys.
function tryParseKnowledgeSearchOutput(text: string): KnowledgeSearchOutput | null {
  let candidate: unknown = null;
  if ((text.startsWith('{') && text.endsWith('}')) ||
      (text.startsWith('[') && text.endsWith(']'))) {
    try {
      candidate = JSON.parse(text);
    } catch {
      return null;
    }
  } else {
    return null;
  }

  candidate = unfoldNestedJson(candidate);
  // Common ADK envelope: { result: { ...real payload... } }
  if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
    const obj = candidate as Record<string, unknown>;
    if (
      'result' in obj &&
      obj.result &&
      typeof obj.result === 'object' &&
      !Array.isArray(obj.result)
    ) {
      candidate = obj.result;
    }
  }

  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return null;
  }
  const o = candidate as Record<string, unknown>;
  // Heuristic: we need at least one of the knowledge_search-shaped keys
  // to assume the structured renderer is the right fit.
  if (!('summary' in o || 'semantic_hits' in o || 'lexical_hits' in o)) {
    return null;
  }
  return {
    summary: typeof o.summary === 'string' ? o.summary : undefined,
    semantic_hits: Array.isArray(o.semantic_hits) ? (o.semantic_hits as KnowledgeSearchHit[]) : [],
    lexical_hits: Array.isArray(o.lexical_hits) ? (o.lexical_hits as KnowledgeSearchHit[]) : [],
    breakdown: (o.breakdown && typeof o.breakdown === 'object' && !Array.isArray(o.breakdown))
      ? (o.breakdown as KnowledgeSearchBreakdown)
      : undefined,
  };
}

function KnowledgeSearchResultView({ data }: { data: KnowledgeSearchOutput }) {
  const { t } = useLanguage();
  const lexical = data.lexical_hits ?? [];
  const semantic = data.semantic_hits ?? [];

  return (
    <div className="ks-result">
      {data.summary && (
        <div className="ks-summary" dir="auto">{data.summary}</div>
      )}
      {data.breakdown && (
        <div className="ks-breakdown-line">
          {data.breakdown.total_chunks ?? 0} total ·
          {' '}{data.breakdown.semantic_only ?? 0} semantic-only ·
          {' '}{data.breakdown.lexical_only ?? 0} lexical-only ·
          {' '}{data.breakdown.both_methods ?? 0} both
        </div>
      )}

      <HitsSection
        title={t('agentDebug.lexicalResults')}
        subtitle={t('agentDebug.lexicalSubtitle')}
        hits={lexical}
        rankKey="fts_rank"
        scoreKey="fts_score"
        emptyMessage={t('agentDebug.lexicalEmpty')}
      />

      <HitsSection
        title={t('agentDebug.semanticResults')}
        subtitle={t('agentDebug.semanticSubtitle')}
        hits={semantic}
        rankKey="semantic_rank"
        scoreKey="semantic_score"
        emptyMessage={t('agentDebug.noVectorMatches')}
      />
    </div>
  );
}

function HitsSection({
  title,
  subtitle,
  hits,
  rankKey,
  scoreKey,
  emptyMessage,
}: {
  title: string;
  subtitle: string;
  hits: KnowledgeSearchHit[];
  rankKey: 'fts_rank' | 'semantic_rank';
  scoreKey: 'fts_score' | 'semantic_score';
  emptyMessage: string;
}) {
  return (
    <section className="ks-section">
      <header className="ks-section-header">
        <span className="ks-section-title">{title}</span>
        <span className="ks-section-count">{hits.length}</span>
        <span className="ks-section-subtitle">{subtitle}</span>
      </header>
      {hits.length === 0 ? (
        <div className="ks-empty">{emptyMessage}</div>
      ) : (
        <ol className="ks-hits">
          {hits.map((h, i) => {
            const rank = h[rankKey] ?? i + 1;
            const score = h[scoreKey];
            return (
              <li key={i} className="ks-hit">
                <div className="ks-hit-head">
                  <span className="ks-hit-rank">#{rank}</span>
                  <span className="ks-hit-file" dir="auto">{h.filename ?? '(unknown file)'}</span>
                  {typeof score === 'number' && (
                    <span className="ks-hit-score">score {formatScore(score)}</span>
                  )}
                </div>
                {h.snippet && (
                  <div className="ks-hit-snippet">{h.snippet}</div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

function formatScore(n: number): string {
  // ts_rank values are tiny (e.g. 0.04); cosine similarity is 0..1.
  // Show with enough precision to distinguish but not overwhelm.
  if (Math.abs(n) >= 1) return n.toFixed(3);
  if (Math.abs(n) >= 0.01) return n.toFixed(3);
  return n.toExponential(2);
}

/* ── Generic RESULT (non-knowledge_search) ─────────── */

function GenericResultView({ text }: { text: string }) {
  // Truncate giant returns so the body never grows past a usable size.
  const MAX_INLINE = 4000;
  const truncated = text.length > MAX_INLINE
    ? text.slice(0, MAX_INLINE) + `\n\n… [truncated ${(text.length - MAX_INLINE).toLocaleString()} more chars — use Copy to get the full payload]`
    : text;
  return <pre className="agent-debug-pre">{truncated}</pre>;
}

/* ── JSON unfold helper (kept — used by KS parser) ── */

function unfoldNestedJson(value: unknown): unknown {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        return unfoldNestedJson(JSON.parse(trimmed));
      } catch { /* not actually JSON — treat as plain string */ }
    }
    return value;
  }
  if (Array.isArray(value)) return value.map(unfoldNestedJson);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = unfoldNestedJson(v);
    }
    return out;
  }
  return value;
}

function safeStringify(v: unknown): string {
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}
